// ** React Imports
import { forwardRef, ReactNode, CSSProperties } from 'react'

// ** Third Party Components
import { Badge } from 'reactstrap'
import classnames from 'classnames'

// ** Types
type ColorType = 
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'info'
  | 'warning'
  | 'dark'
  | 'light-primary'
  | 'light-secondary'
  | 'light-success'
  | 'light-danger'
  | 'light-info'
  | 'light-warning'
  | 'light-dark'

type SizeType = 'sm' | 'lg' | 'xl'
type StatusType = 'online' | 'offline' | 'away' | 'busy'

/**
 * Avatar component props
 * 
 * Note: When 'initials' prop is true, 'content' prop is required and must be a string.
 * This validation is handled at runtime in the component.
 */
export interface AvatarProps {
  imgClassName?: string
  className?: string
  src?: string
  tag?: React.ComponentType<any> | string
  badgeUp?: boolean
  content?: string
  icon?: ReactNode
  contentStyles?: CSSProperties
  badgeText?: string
  imgHeight?: string | number
  imgWidth?: string | number
  size?: SizeType
  status?: StatusType
  badgeColor?: ColorType
  color?: ColorType
  initials?: boolean
  img?: string | boolean
  [key: string]: any
}

const Avatar = forwardRef<HTMLElement, AvatarProps>((props, ref) => {
  // ** Props
  const {
    color,
    className,
    imgClassName,
    initials,
    size,
    badgeUp,
    content,
    icon,
    badgeColor,
    badgeText,
    img,
    imgHeight,
    imgWidth,
    status,
    tag: Tag = 'div',
    contentStyles,
    ...rest
  } = props

  // Runtime validation for initials prop
  if (initials && content === undefined) {
    console.error('Avatar: content prop is required with initials prop.')
  }
  if (initials && typeof content !== 'string') {
    console.error('Avatar: content prop must be a string.')
  }
  if (typeof initials !== 'boolean' && initials !== undefined) {
    console.error('Avatar: initials must be a boolean!')
  }

  // ** Function to extract initials from content
  const getInitials = (str: string): string => {
    const results: string[] = []
    const wordArray = str.split(' ')
    wordArray.forEach(e => {
      results.push(e[0])
    })
    return results.join('')
  }

  return (
    <Tag
      className={classnames('avatar', {
        [className || '']: className,
        [`bg-${color}`]: color,
        [`avatar-${size}`]: size
      })}
      ref={ref}
      {...rest}
    >
      {img === false || img === undefined ? (
        <span
          className={classnames('avatar-content', {
            'position-relative': badgeUp
          })}
          style={contentStyles}
        >
          {initials && content ? getInitials(content) : content}

          {icon ? icon : null}
          {badgeUp ? (
            <Badge color={badgeColor ? badgeColor : 'primary'} className='badge-sm badge-up' pill>
              {badgeText ? badgeText : '0'}
            </Badge>
          ) : null}
        </span>
      ) : (
        <img
          className={classnames({
            [imgClassName || '']: imgClassName
          })}
          src={img as string}
          alt='avatarImg'
          height={imgHeight && !size ? imgHeight : 32}
          width={imgWidth && !size ? imgWidth : 32}
        />
      )}
      {status ? (
        <span
          className={classnames({
            [`avatar-status-${status}`]: status,
            [`avatar-status-${size}`]: size
          })}
        ></span>
      ) : null}
    </Tag>
  )
})

export default Avatar
